// Field-level diff for a single record. Produces a flat list of FieldChange
// entries describing what changed between two record snapshots. Recurses into
// nested objects; treats arrays of primitives as set diffs; arrays of objects
// are stringify-compared as a fallback.

export interface FieldChange {
    // Path as an array of key segments — NOT a dotted string. Storing as
    // an array means a literal-dot key (e.g. a `values` dict key called
    // `"building.barracks"`) is one segment and doesn't collide with
    // genuine nesting when the Resolve flow walks the record.
    path: string[];
    kind: 'scalar' | 'set' | 'opaque';
    // RAW values — what will actually be written into state if the user
    // resolves this difference. Stays as uids / numbers / nulls etc.
    current: unknown;
    target: unknown;
    // For 'set' kind only — what entries were added (in target, not current)
    // and removed (in current, not target) when comparing as sets of strings.
    added?: string[];
    removed?: string[];
    // DISPLAY values — set by the localization pass for the renderer. Same
    // shape as the raw fields but with uids resolved to friendly names and
    // ambiguous-label disambiguation applied. The Resolve action uses the
    // raw fields; the renderer uses these.
    displayPath?: string;
    displayCurrent?: unknown;
    displayTarget?: unknown;
    displayAdded?: string[];
    displayRemoved?: string[];
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isPrimitiveArray(arr: unknown[]): boolean {
    return arr.every(v => v === null || ['string', 'number', 'boolean'].includes(typeof v));
}

// A primitive *matrix* — an array whose every element is itself a primitive
// array (e.g. a building's `rewards: string[][]`, indexed by level). An empty
// array qualifies vacuously so an empty side still pairs index-wise against a
// populated one. Diffed per index as a set so the per-level assignment reads
// cleanly instead of collapsing to one opaque blob.
function isPrimitiveMatrix(arr: unknown[]): boolean {
    return arr.every(v => Array.isArray(v) && isPrimitiveArray(v));
}

// An array of objects that each carry a branch-local `uid` (e.g. a building's
// `passiveRewards`). Compared ignoring uid and order so pure uid regeneration
// or reordering stays silent and only real content changes surface.
function isUidObjectArray(arr: unknown[]): boolean {
    return arr.length > 0 && arr.every(v => isPlainObject(v) && typeof v.uid === 'string');
}

function stripUidSignatures(arr: Record<string, unknown>[]): string[] {
    return arr.map(o => {
        const {uid, ...rest} = o;
        return JSON.stringify(rest);
    }).sort();
}

function setDiff(curr: unknown[], targ: unknown[]): { added: string[]; removed: string[] } {
    const c = new Set(curr.map(v => String(v)));
    const t = new Set(targ.map(v => String(v)));
    const added: string[] = [];
    const removed: string[] = [];
    t.forEach(v => { if (!c.has(v)) added.push(v); });
    c.forEach(v => { if (!t.has(v)) removed.push(v); });
    added.sort();
    removed.sort();
    return {added, removed};
}

export function diffRecord(current: unknown, target: unknown, path: string[] = []): FieldChange[] {
    const out: FieldChange[] = [];

    // Both objects — walk the union of keys.
    if (isPlainObject(current) && isPlainObject(target)) {
        const keys = new Set([...Object.keys(current), ...Object.keys(target)]);
        const sortedKeys = Array.from(keys).sort();
        for (const k of sortedKeys) {
            out.push(...diffRecord(current[k], target[k], [...path, k]));
        }
        return out;
    }

    // Both arrays.
    if (Array.isArray(current) && Array.isArray(target)) {
        if (isPrimitiveArray(current) && isPrimitiveArray(target)) {
            const {added, removed} = setDiff(current, target);
            if (added.length > 0 || removed.length > 0) {
                out.push({path, kind: 'set', current, target, added, removed});
            }
        } else if (isPrimitiveMatrix(current) && isPrimitiveMatrix(target)) {
            // Primitive matrix (e.g. building rewards: string[][], indexed by
            // level). Emit one set change per index so each level's added /
            // removed entries are shown and resolved on their own — a missing
            // index on either side pairs against an empty list.
            const len = Math.max(current.length, target.length);
            for (let i = 0; i < len; i++) {
                const c = Array.isArray(current[i]) ? current[i] as unknown[] : [];
                const t = Array.isArray(target[i]) ? target[i] as unknown[] : [];
                const {added, removed} = setDiff(c, t);
                if (added.length > 0 || removed.length > 0) {
                    out.push({path: [...path, String(i)], kind: 'set', current: c, target: t, added, removed});
                }
            }
        } else if (isUidObjectArray(current) && isUidObjectArray(target)) {
            // Array of uid-bearing objects (e.g. passiveRewards). Compare by a
            // uid-stripped, order-insensitive signature so uid churn / reorders
            // are silent; surface a (non-resolvable) opaque change with the raw
            // values only when the content actually differs.
            const cs = stripUidSignatures(current as Record<string, unknown>[]);
            const ts = stripUidSignatures(target as Record<string, unknown>[]);
            if (JSON.stringify(cs) !== JSON.stringify(ts)) {
                out.push({path, kind: 'opaque', current, target});
            }
        } else {
            // Mixed or other array-of-objects. Fallback to stringify comparison.
            const cs = JSON.stringify(current);
            const ts = JSON.stringify(target);
            if (cs !== ts) {
                out.push({path, kind: 'opaque', current, target});
            }
        }
        return out;
    }

    // Mixed shapes (one side is undefined, primitive vs object, etc.). Treat
    // as a scalar change as long as they aren't equal.
    if (current !== target) {
        // JSON.stringify equality catches deep equality of any remaining
        // structurally-equal objects, just in case.
        if (JSON.stringify(current) !== JSON.stringify(target)) {
            out.push({path, kind: 'scalar', current, target});
        }
    }

    return out;
}

// True if two records are deep-equal under JSON serialization. Cheap "do they
// differ at all?" check used to bucket records into onlyIn/differs.
export function recordsEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}
